(() => {
  const qsa = (selector, context = document) => Array.from(context.querySelectorAll(selector));
  const qs = (selector, context = document) => context.querySelector(selector);
  const config = window.Weckify || {};
  const routes = config.routes || {};
  let lastFocusedElement = null;

  const escapeHTML = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  }[character]));

  const formatMoney = (cents = 0) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: config.currency || 'USD'
      }).format(Number(cents) / 100);
    } catch (error) {
      return `${(Number(cents) / 100).toFixed(2)} ${config.currency || ''}`.trim();
    }
  };
  const sizedImageURL = (url, width) => `${url}${String(url).includes('?') ? '&' : '?'}width=${width}`;

  const focusableElements = (container) => qsa('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', container).filter((element) => !element.hidden);
  const lockPage = () => document.body.classList.add('overflow-hidden');
  const unlockPage = () => { if (!qs('.is-open')) document.body.classList.remove('overflow-hidden'); };

  const closeLayer = (layer, restoreFocus = true) => {
    if (!layer) return;
    layer.classList.remove('is-open');
    qsa(`[aria-controls="${layer.id}"]`).forEach((button) => button.setAttribute('aria-expanded', 'false'));
    window.setTimeout(() => {
      layer.hidden = true;
      unlockPage();
      if (restoreFocus) lastFocusedElement?.focus();
    }, 220);
  };

  const openLayer = (layer, trigger) => {
    if (!layer) return;
    qsa('.is-open').forEach((openItem) => { if (openItem !== layer) closeLayer(openItem, false); });
    lastFocusedElement = trigger || document.activeElement;
    layer.hidden = false;
    requestAnimationFrame(() => layer.classList.add('is-open'));
    trigger?.setAttribute('aria-expanded', 'true');
    lockPage();
    window.setTimeout(() => focusableElements(layer)[0]?.focus(), 40);
  };

  document.addEventListener('click', (event) => {
    const menuTrigger = event.target.closest('[data-drawer-open]');
    if (menuTrigger) {
      openLayer(document.getElementById(menuTrigger.getAttribute('aria-controls')), menuTrigger);
      return;
    }
    const menuClose = event.target.closest('[data-drawer-close]');
    if (menuClose && !event.target.closest('[data-search-open]')) closeLayer(menuClose.closest('[data-drawer]'));
    if (event.target.matches('[data-drawer-overlay]')) closeLayer(event.target.closest('[data-drawer]'));

    const searchTrigger = event.target.closest('[data-search-open]');
    if (searchTrigger) {
      event.preventDefault();
      const menu = searchTrigger.closest('[data-drawer]');
      if (menu) closeLayer(menu, false);
      openLayer(qs('[data-search-overlay]'), searchTrigger);
      window.setTimeout(() => qs('[data-predictive-input]')?.focus(), 80);
    }
    const searchClose = event.target.closest('[data-search-close]');
    if (searchClose) closeLayer(searchClose.closest('[data-search-overlay]'));

    const cartTrigger = event.target.closest('[data-cart-open]');
    if (cartTrigger && config.cartDrawer) {
      event.preventDefault();
      openCart(cartTrigger);
    }
    const cartClose = event.target.closest('[data-cart-close]');
    if (cartClose) closeLayer(cartClose.closest('[data-cart-drawer]'));
  });

  document.addEventListener('keydown', (event) => {
    const layer = qs('.is-open');
    if (!layer) return;
    if (event.key === 'Escape') { closeLayer(layer); return; }
    if (event.key !== 'Tab') return;
    const elements = focusableElements(layer);
    if (!elements.length) return;
    const first = elements[0];
    const last = elements[elements.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });

  const initializeStickyHeader = (header) => {
    if (header.dataset.stickyReady === 'true') return;
    header.dataset.stickyReady = 'true';
    const sectionWrapper = header.closest('.shopify-section');
    const groupWrapper = header.closest('.shopify-section-group-header-group');
    const stickyTarget = groupWrapper || sectionWrapper;
    stickyTarget?.classList.add('shopify-section--sticky-header');
    const updateHeight = () => document.documentElement.style.setProperty('--sticky-header-height', `${stickyTarget?.getBoundingClientRect().height || header.getBoundingClientRect().height}px`);
    updateHeight();
    if ('ResizeObserver' in window && stickyTarget) new ResizeObserver(updateHeight).observe(stickyTarget);
    const update = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    update();
    window.addEventListener('scroll', update, { passive: true });
  };
  qsa('[data-sticky-header]').forEach(initializeStickyHeader);
  document.addEventListener('shopify:section:load', (event) => {
    const headers = qsa('[data-sticky-header]', event.target);
    const groupWrapper = event.target.closest?.('.shopify-section-group-header-group');
    if (!headers.length && groupWrapper && !groupWrapper.querySelector('[data-sticky-header]')) {
      groupWrapper.classList.remove('shopify-section--sticky-header');
      document.documentElement.style.removeProperty('--sticky-header-height');
    }
    headers.forEach(initializeStickyHeader);
  });

  document.addEventListener('click', (event) => {
    const thumbnail = event.target.closest('[data-gallery-thumb]');
    if (!thumbnail) return;
    const gallery = thumbnail.closest('[data-product-gallery]');
    const target = gallery?.querySelector(`[data-gallery-media="${thumbnail.dataset.galleryThumb}"]`);
    if (!target) return;
    qsa('[data-gallery-media]', gallery).forEach((item) => { item.hidden = item !== target; });
    qsa('[data-gallery-thumb]', gallery).forEach((item) => item.classList.toggle('is-active', item === thumbnail));
  });

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.productForm);
      this.variantSelect = this.form?.querySelector('[name="id"]');
      try { this.variants = JSON.parse(qs('[data-variant-json]', this)?.textContent || '[]'); }
      catch (error) { this.variants = []; }
      this.addEventListener('change', () => this.update());
      this.update();
    }

    selectedOptions() {
      return qsa('[data-option-group]', this).map((group) => qs('input:checked, select', group)?.value);
    }

    updateAvailability(selectedOptions) {
      qsa('[data-option-group]', this).forEach((group, position) => {
        qsa('input[type="radio"]', group).forEach((input) => {
          const possible = this.variants.some((variant) => variant.available && variant.options.every((value, index) => index === position ? value === input.value : value === selectedOptions[index]));
          input.disabled = !possible && !input.checked;
          input.closest('.option-pill')?.classList.toggle('is-unavailable', !possible);
        });
        const label = qs('[data-option-value]', group);
        if (label) label.textContent = selectedOptions[position] || '';
      });
    }

    update() {
      if (!this.form || !this.variantSelect) return;
      const selectedOptions = this.selectedOptions();
      this.updateAvailability(selectedOptions);
      const variant = this.variants.find((item) => item.options.every((value, index) => value === selectedOptions[index]));
      const button = qs('[name="add"]', this.form);
      const buttonLabel = qs('span', button);
      if (!variant) {
        if (button) button.disabled = true;
        if (buttonLabel) buttonLabel.textContent = this.dataset.unavailableText;
        this.updateSticky(null);
        return;
      }
      this.variantSelect.value = variant.id;
      const option = this.variantSelect.querySelector(`option[value="${variant.id}"]`);
      if (button) button.disabled = !variant.available;
      if (buttonLabel) buttonLabel.textContent = variant.available ? this.dataset.addText : this.dataset.soldOutText;
      this.updatePrice(option);
      this.updateMedia(variant);
      this.updateSticky(option, variant.available);
      if (this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }

    updatePrice(option) {
      const section = this.closest('[data-product-section]');
      const price = qs('[data-product-price]', section);
      const compare = qs('[data-compare-price]', section);
      const sale = qs('[data-sale-badge]', section);
      if (price && option?.dataset.price) price.textContent = option.dataset.price;
      const compareCents = Number(option?.dataset.compareCents || 0);
      const priceCents = Number(option?.dataset.priceCents || 0);
      if (compare) { compare.textContent = option?.dataset.compare || ''; compare.hidden = compareCents <= priceCents; }
      if (sale) {
        const saving = compareCents > priceCents ? Math.round(((compareCents - priceCents) / compareCents) * 100) : 0;
        sale.textContent = saving ? `${saving}% off` : '';
        sale.hidden = !saving;
      }
    }

    updateMedia(variant) {
      const mediaId = variant?.featured_media?.id || variant?.featured_image?.id;
      if (!mediaId) return;
      const gallery = qs('[data-product-gallery]', this.closest('[data-product-section]'));
      gallery?.querySelector(`[data-gallery-thumb="${mediaId}"]`)?.click();
    }

    updateSticky(option, available = false) {
      const sticky = qs(`[data-sticky-atc][data-product-form="${this.dataset.productForm}"]`);
      if (!sticky) return;
      const stickyButton = qs('[data-sticky-submit]', sticky);
      const stickyPrice = qs('[data-sticky-price]', sticky);
      if (stickyButton) stickyButton.disabled = !available;
      if (stickyPrice && option?.dataset.price) stickyPrice.textContent = option.dataset.price;
    }
  }
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url) return;
      fetch(this.dataset.url).then((response) => response.text()).then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const replacement = html.querySelector('product-recommendations');
        if (replacement?.innerHTML.trim()) this.innerHTML = replacement.innerHTML;
      }).catch(() => {});
    }
  }
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

  document.addEventListener('click', (event) => {
    const quantityButton = event.target.closest('[data-quantity-change]');
    if (quantityButton) {
      const input = quantityButton.parentElement.querySelector('input[type="number"]');
      if (!input) return;
      const step = Number(input.step || 1);
      const next = Number(input.value || 0) + (quantityButton.dataset.quantityChange === 'plus' ? step : -step);
      input.value = Math.max(Number(input.min || 0), next);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    const stickyButton = event.target.closest('[data-sticky-submit]');
    if (stickyButton) document.getElementById(stickyButton.closest('[data-sticky-atc]').dataset.productForm)?.requestSubmit();
  });

  qsa('[data-sticky-atc]').forEach((sticky) => {
    const form = document.getElementById(sticky.dataset.productForm);
    const mainButton = qs('[name="add"]', form);
    if (!mainButton || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(([entry]) => { sticky.hidden = entry.isIntersecting; }, { threshold: 0 });
    observer.observe(mainButton);
  });

  let predictiveTimer;
  const predictiveInput = qs('[data-predictive-input]');
  predictiveInput?.addEventListener('input', () => {
    window.clearTimeout(predictiveTimer);
    predictiveTimer = window.setTimeout(() => loadPredictiveSearch(predictiveInput.value.trim()), 220);
  });

  async function loadPredictiveSearch(query) {
    const results = qs('[data-predictive-results]');
    const status = qs('[data-predictive-status]');
    if (!results) return;
    if (query.length < 2) { results.innerHTML = ''; if (status) status.textContent = ''; return; }
    if (status) status.textContent = 'Loading search suggestions';
    try {
      const parameters = new URLSearchParams({ q: query, 'resources[type]': 'product,collection,page,article', 'resources[limit]': '6', 'resources[options][unavailable_products]': 'last' });
      const response = await fetch(`${routes.predictiveSearch}.json?${parameters.toString()}`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Search failed');
      const payload = await response.json();
      const resources = payload?.resources?.results || {};
      const items = [
        ...(resources.products || []).map((item) => ({ ...item, kind: 'Products' })),
        ...(resources.collections || []).map((item) => ({ ...item, kind: 'Collections' })),
        ...(resources.pages || []).map((item) => ({ ...item, kind: 'Pages' })),
        ...(resources.articles || []).map((item) => ({ ...item, kind: 'Articles' }))
      ];
      results.innerHTML = items.length ? items.map((item) => {
        const image = item.featured_image?.url || item.image || '';
        return `<a class="predictive-search__item" href="${escapeHTML(item.url)}">${image ? `<img src="${escapeHTML(sizedImageURL(image, 360))}" alt="" width="360" height="360" loading="lazy">` : ''}<span class="eyebrow">${escapeHTML(item.kind)}</span><strong>${escapeHTML(item.title)}</strong>${item.price ? `<span>${escapeHTML(item.price)}</span>` : ''}</a>`;
      }).join('') : '<p class="predictive-search__empty">No suggestions found. Press Enter to see all results.</p>';
      if (status) status.textContent = `${items.length} suggestions available`;
    } catch (error) {
      results.innerHTML = '<p class="predictive-search__empty">Search suggestions are temporarily unavailable. Press Enter to search.</p>';
      if (status) status.textContent = 'Search suggestions unavailable';
    }
  }

  async function fetchCart() {
    const response = await fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart request failed');
    return response.json();
  }

  async function openCart(trigger) {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;
    openLayer(drawer, trigger);
    try { renderCart(await fetchCart()); }
    catch (error) { setCartStatus('Unable to load the cart.'); }
  }

  function setCartStatus(message = '') {
    const status = qs('[data-cart-status]');
    if (status) status.textContent = message;
  }

  function renderCart(cart) {
    const container = qs('[data-cart-items]');
    const empty = qs('[data-cart-empty]');
    const footer = qs('[data-cart-footer]');
    if (!container) return;
    if (cart.currency) config.currency = cart.currency;
    qsa('[data-cart-count]').forEach((count) => { count.textContent = cart.item_count; count.classList.toggle('hidden', cart.item_count === 0); });
    const countLabel = qs('[data-cart-count-label]');
    if (countLabel) countLabel.textContent = cart.item_count ? `(${cart.item_count})` : '';
    empty?.classList.toggle('hidden', cart.item_count > 0);
    footer?.classList.toggle('hidden', cart.item_count === 0);
    qsa('.cart-drawer__item', container).forEach((item) => item.remove());
    cart.items.forEach((item, index) => {
      const image = item.featured_image?.url || item.image || '';
      const properties = Object.entries(item.properties || {}).filter(([key, value]) => value && !key.startsWith('_')).map(([key, value]) => `${escapeHTML(key)}: ${escapeHTML(value)}`).join('<br>');
      const row = document.createElement('article');
      row.className = 'cart-drawer__item';
      row.dataset.line = index + 1;
      row.innerHTML = `${image ? `<a href="${escapeHTML(item.url)}"><img src="${escapeHTML(sizedImageURL(image, 220))}" alt="${escapeHTML(item.product_title)}" width="180" height="180" loading="lazy"></a>` : '<div></div>'}<div><div class="cart-drawer__item-head"><a class="cart-drawer__item-title" href="${escapeHTML(item.url)}">${escapeHTML(item.product_title)}</a><strong>${formatMoney(item.final_line_price)}</strong></div>${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-drawer__item-meta">${escapeHTML(item.variant_title)}${properties ? `<br>${properties}` : ''}</p>` : properties ? `<p class="cart-drawer__item-meta">${properties}</p>` : ''}<div class="cart-drawer__line-bottom"><div class="cart-drawer__quantity"><button type="button" data-cart-change="${index + 1}" data-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">−</button><span>${item.quantity}</span><button type="button" data-cart-change="${index + 1}" data-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button></div><button class="cart-drawer__remove" type="button" data-cart-change="${index + 1}" data-quantity="0">${escapeHTML(config.strings?.remove || 'Remove')}</button></div></div>`;
      container.appendChild(row);
    });
    const total = qs('[data-cart-total]');
    if (total) total.textContent = formatMoney(cart.total_price);
    const progress = qs('[data-shipping-progress]');
    if (progress) {
      const threshold = Number(progress.dataset.threshold || 0);
      const remaining = Math.max(0, threshold - cart.total_price);
      const bar = qs('[data-shipping-bar]', progress);
      const message = qs('[data-shipping-message]', progress);
      if (bar) bar.style.width = `${threshold ? Math.min(100, (cart.total_price / threshold) * 100) : 100}%`;
      if (message) message.textContent = remaining > 0 ? `${formatMoney(remaining)} away from free shipping.` : 'You have unlocked free shipping.';
    }
  }

  document.addEventListener('click', async (event) => {
    const changeButton = event.target.closest('[data-cart-change]');
    if (!changeButton) return;
    changeButton.disabled = true;
    setCartStatus('Updating cart');
    try {
      const response = await fetch(`${routes.cartChange}.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(changeButton.dataset.cartChange), quantity: Number(changeButton.dataset.quantity) })
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || 'Cart update failed');
      renderCart(cart); setCartStatus('Cart updated');
    } catch (error) {
      setCartStatus(error.message || 'Unable to update the cart.');
      changeButton.disabled = false;
    }
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form || !config.cartDrawer) return;
    if (event.submitter && event.submitter.name !== 'add') return;
    event.preventDefault();
    const submitButton = event.submitter || qs('[type="submit"]', form);
    const wasDisabled = submitButton?.disabled || false;
    submitButton?.setAttribute('aria-busy', 'true');
    if (submitButton) submitButton.disabled = true;
    try {
      const response = await fetch(`${routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || 'Unable to add this item.');
      renderCart(await fetchCart());
      openLayer(qs('[data-cart-drawer]'), submitButton);
      setCartStatus('Added to your bag.');
    } catch (error) {
      setCartStatus(error.message || 'Unable to add this item.');
      openLayer(qs('[data-cart-drawer]'), submitButton);
    } finally {
      submitButton?.removeAttribute('aria-busy');
      if (submitButton) submitButton.disabled = wasDisabled;
    }
  });

  qsa('[data-testimonial-slider]').forEach((slider) => {
    const slides = qsa('[data-slide]', slider);
    if (slides.length < 2) return;
    let index = 0;
    const show = (nextIndex) => {
      index = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => slide.classList.toggle('hidden', slideIndex !== index));
    };
    qs('[data-slider-prev]', slider)?.addEventListener('click', () => show(index - 1));
    qs('[data-slider-next]', slider)?.addEventListener('click', () => show(index + 1));
  });
})();
